import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, UploadCloud, X, ChevronDown } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../../../components/ui/dropdown-menu";
import PageHeader from "../../../components/shared/PageHeader";
import useMediaQuery from "../../../components/shared/useMediaQuery";

// NOTE: every field below is wired to the same `formData.discription` key and
// "Open a Ticket" has no submit handler in the original page — it isn't
// linked from anywhere in the app (the FAQ page's link to it is commented
// out) and reads as an unfinished stub rather than a page in real use.
// Preserved exactly as-is; only the presentation layer changed here.
const ISSUE_CATEGORY_OPTIONS = ["ACH", "PayPal"];

function FieldLabel({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", color: "#c3c8cc", marginBottom: 6 }}>
      {children}
    </div>
  );
}

export default function MemberCreateTicketPage() {
  const navigate = useNavigate();
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [formData, setFormData] = useState({ discription: "", issue_category: "" });
  const isMobile = useMediaQuery("(max-width: 640px)");

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) setUploadedFile(file);
  };
  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setUploadedFile(files);
      e.target.value = "";
    }
  };

  const handleFormData = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isDisableButton = !formData?.discription || !formData?.issue_category;

  return (
    <div style={{ width: "100%", maxWidth: 520, margin: "0 auto", display: "flex", flexDirection: "column", gap: isMobile ? 16 : 20 }}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.6)",
          fontSize: 13,
          cursor: "pointer",
          padding: 0,
          width: "fit-content",
        }}
      >
        <ArrowLeft size={15} /> Back
      </button>

      <PageHeader title="Contact Support" subtitle="Submit your issue to our support team via Discord." />

      <div
        style={{
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 16,
          padding: isMobile ? 18 : 22,
          background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div>
          <FieldLabel>Item Link</FieldLabel>
          <Input type="text" name="discription" value={formData.discription} onChange={handleFormData} placeholder="Enter Item Link" />
        </div>
        <div>
          <FieldLabel>Shipping Address</FieldLabel>
          <Input type="text" name="discription" value={formData.discription} onChange={handleFormData} placeholder="Enter Item Link" />
        </div>
        <div>
          <FieldLabel>Item UPC/GTIN</FieldLabel>
          <Input type="text" name="discription" value={formData.discription} onChange={handleFormData} placeholder="Enter Item UPC" />
        </div>
        <div>
          <FieldLabel>Quantity</FieldLabel>
          <Input type="number" name="discription" value={formData.discription} onChange={handleFormData} placeholder="Describe your issue in detail..." />
        </div>
        <div>
          <FieldLabel>Issue/Query Description</FieldLabel>
          <textarea
            name="discription"
            value={formData.discription}
            onChange={handleFormData}
            placeholder="Describe your issue in detail..."
            rows={4}
            style={{
              width: "100%",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.03)",
              color: "#fff",
              padding: "10px 12px",
              fontSize: 13.5,
              fontFamily: "inherit",
              resize: "vertical",
            }}
          />
        </div>
        <div>
          <FieldLabel>Issue Category</FieldLabel>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  height: 36,
                  padding: "0 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.1)",
                  background: "rgba(255,255,255,0.03)",
                  color: formData.issue_category ? "#fff" : "#c3c8cc",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                {formData.issue_category || "Select category"}
                <ChevronDown size={14} color="#c3c8cc" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent style={{ minWidth: 200 }}>
              {ISSUE_CATEGORY_OPTIONS.map((option) => (
                <DropdownMenuItem key={option} onSelect={() => setFormData((prev) => ({ ...prev, issue_category: option }))}>
                  {option}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {uploadedFile === null ? (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById("ticket-file-input").click()}
            style={{
              minHeight: 150,
              borderRadius: 12,
              border: `1.5px dashed ${dragOver ? "rgba(0,173,181,0.5)" : "rgba(255,255,255,0.16)"}`,
              background: dragOver ? "rgba(0,173,181,0.06)" : "transparent",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: isMobile ? 14 : 16,
              transition: "border-color 0.15s, background 0.15s",
            }}
          >
            <UploadCloud size={24} color="#c3c8cc" />
            <span style={{ fontSize: 13, color: "#fff", fontWeight: 600, textAlign: "center" }}>Click here or drop a file to upload receipt</span>
            <span style={{ fontSize: 11.5, color: "#c3c8cc", textAlign: "center" }}>(Accepted File Types: .jpg, .png, .pdf)</span>
            <input id="ticket-file-input" type="file" accept=".jpg,.png,.pdf" style={{ display: "none" }} onChange={handleFileInputChange} />
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#fff", marginBottom: 8 }}>Uploaded Files:</div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: isMobile ? "flex-start" : "center",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? 8 : 12,
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 10,
                padding: "10px 14px",
              }}
            >
              <span style={{ fontSize: 13, color: "#c3c8cc", maxWidth: "100%", overflowWrap: "anywhere" }}>{uploadedFile?.name || uploadedFile?.[0]?.name}</span>
              <button
                type="button"
                onClick={() => setUploadedFile(null)}
                style={{ background: "transparent", border: "none", color: "#f27059", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 12.5 }}
              >
                <X size={13} /> Remove
              </button>
            </div>
          </div>
        )}

        <Button disabled={isDisableButton} style={{ width: "100%" }}>
          Open a Ticket
        </Button>
      </div>
    </div>
  );
}
